"use client";

import { useEffect, useState } from "react";
import { QrCodeIcon } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import { isWindow } from "@/service/render-environment";
import { getAssetImage } from "@/service/image/asset";
import { QrAppleBadge } from "@/components/security/login/components/qr-auth/qr-apple-badge";
import { QrGoogleBadge } from "@/components/security/login/components/qr-auth/qr-google-badge";
import { Separator } from "@/components/ui/separator";
import { Button } from "@/components/ui/button";
import { Dialog, DialogClose, DialogContent } from "@/components/ui/dialog";
import { redirectAfterLogin } from "../../service";

export const QrAuth = () => {
  const [isOpenModal, setIsOpenModal] = useState(false);
  const [logged, setLogged] = useState(false);
  const [hash] = useState(
    `https://remedium.md/in-app/qr/auth/${crypto.randomUUID()}`,
  );

  useEffect(() => {
    if (!isOpenModal || logged) {
      return;
    }

    const interval = setInterval(async () => {
      try {
        const response = await fetch("/login/qr-auth", {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ token: hash }),
        });

        if (response.ok) {
          setLogged(true);
          redirectAfterLogin();
        }
      } catch (e) {
        console.error(e);
      }
    }, 2000);
    return () => clearInterval(interval);
  }, [hash, logged, isOpenModal]);

  if (isWindow() && window.innerWidth < 768) {
    return null;
  }

  return (
    <>
      <div
        className={`text-muted-foreground my-6 flex h-2.5 w-full items-center justify-between text-xs font-normal`}
      >
        <Separator className={"w-[calc(50%-126px)]!"} />
        Logujesz się na komputerze służbowym?{" "}
        <Separator className={"w-[calc(50%-126px)]!"} />
      </div>
      <Button
        type={"button"}
        variant={"outline"}
        className={"w-full"}
        onClick={() => setIsOpenModal(true)}
      >
        <QrCodeIcon size={"16px"} />
        Bezpieczne logowanie QR kod
      </Button>

      <Dialog open={isOpenModal} onOpenChange={() => setIsOpenModal(false)}>
        <DialogContent className={"h-[637px] w-[536px] overflow-hidden"}>
          <div className="h-[249px] items-end justify-center bg-[#f0f6fe]">
            <img
              src={`${getAssetImage("/qr-auth/graphic")}.png`}
              alt={"qr code"}
              className={"mx-auto h-full"}
            />
          </div>
          <div className={`m-8 text-2xl font-semibold text-black`}>
            Zaloguj się bez wpisywania danych
          </div>

          <div className="flex flex-col justify-center gap-8 bg-white px-4 py-4 text-left md:flex-row md:px-[54px]">
            <div className={"flex items-center justify-center"}>
              <div className="flex h-[112px] items-center rounded-lg bg-white p-2 shadow-[44.131px_66.456px_32.189px_0px_rgba(68,32,126,0.01),24.921px_37.381px_26.998px_0px_rgba(68,32,126,0.05),10.903px_16.614px_19.729px_0px_rgba(30,31,45,0.09),2.596px_4.153px_10.903px_0px_rgba(68,32,126,0.08)]">
                <QRCodeSVG value={hash} width={"96px"} />
              </div>
            </div>
            <div className={`text-sm font-normal text-black`}>
              <p className={"mb-4"}>
                <strong>Zeskanuj kod QR</strong>, dotykając ikony{" "}
                <QrCodeIcon size={16} className={"inline translate-y-[2px]"} />{" "}
                w aplikacji mobilnej. Znajdziesz ją w prawym górnym rogu ekranu
                głównego przy powiadomieniach.
              </p>
              <p>
                Jeśli nie posiadasz aplikacji Remedium, pobierz ją przez{" "}
                <strong>Google Play lub App Store</strong>.
              </p>
            </div>
          </div>
          <div
            className={
              "mt-8 flex items-center justify-center gap-4 [&_svg]:h-auto [&_svg]:w-[110px]"
            }
          >
            <a
              href={"https://apps.apple.com/pl/app/remedium/id1666340112?l=pl"}
            >
              <QrAppleBadge />
            </a>
            <a
              href={
                "https://play.google.com/store/apps/details?id=md.remedium.app"
              }
            >
              <QrGoogleBadge />
            </a>
          </div>
          <DialogClose />
        </DialogContent>
      </Dialog>
    </>
  );
};
